import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3 text-left" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs text-neutral50 m-0" }, [
  /*#__PURE__*/_createTextVNode(" Define qual fonte vence quando há mais de um registro para a mesma perna. "),
  /*#__PURE__*/_createElementVNode("b", null, "Check-in e check-out são resolvidos separadamente"),
  /*#__PURE__*/_createTextVNode(" — o horário efetivo pode vir de fontes diferentes no mesmo plantão. Arraste para reordenar (ou use ↑/↓ com o item focado). ")
], -1)
const _hoisted_3 = ["data-draggable"]
const _hoisted_4 = ["data-testid", "draggable", "tabindex", "aria-label", "onKeydown"]
const _hoisted_5 = { class: "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-primary5 text-xs font-extrabold text-primary40" }
const _hoisted_6 = { class: "flex flex-col" }
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = { class: "text-xs text-neutral50/70" }
const _hoisted_9 = ["data-testid"]
const _hoisted_10 = { class: "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-gray-200 text-xs font-extrabold text-gray-500" }
const _hoisted_11 = { class: "flex flex-col" }
const _hoisted_12 = ["data-testid"]
const _hoisted_13 = { class: "text-xs text-gray-400" }
const _hoisted_14 = {
  key: 0,
  class: "flex flex-wrap items-center gap-3"
}
const _hoisted_15 = /*#__PURE__*/_createElementVNode("span", { class: "text-xs text-neutral50 m-0" }, [
  /*#__PURE__*/_createTextVNode(" Empate entre duas fontes do mesmo nível: vence o registro "),
  /*#__PURE__*/_createElementVNode("b", null, "mais recente"),
  /*#__PURE__*/_createTextVNode(". ")
], -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("div", {
  class: "flex gap-2 rounded-lg border border-negative/40 bg-negative/5 px-3 py-2.5 text-xs text-negative",
  "data-testid": "source-precedence-impact-warning"
}, [
  /*#__PURE__*/_createElementVNode("span", { "aria-hidden": "true" }, "⚠️"),
  /*#__PURE__*/_createElementVNode("span", null, [
    /*#__PURE__*/_createElementVNode("b", null, "Alterar a precedência muda o horário efetivo de plantões futuros e o valor faturado."),
    /*#__PURE__*/_createTextVNode(" A ordem passa a valer para eventos posteriores ao salvamento — plantões já resolvidos não são recalculados. ")
  ])
], -1)

import { computed } from 'vue'
import draggable from 'vuedraggable'
import { mdiDragVertical, mdiLock } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import {
  DEFAULT_SOURCE_PRECEDENCE,
  PINNED_SOURCE_KIND,
  SOURCE_PRECEDENCE_DESCRIPTIONS,
  SOURCE_PRECEDENCE_NAMES,
  sanitizeSourcePrecedence
} from './sourcePrecedence'

/**
 * Ranking de precedência das fontes do horário efetivo (M4, seção "Precedência das
 * fontes"). Reordenável por drag & drop E por teclado (↑/↓ com o item focado) — o
 * drag sozinho deixaria a configuração inacessível a quem navega por teclado.
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'SourcePrecedenceList',
  props: {
    modelValue: { default: () => [...DEFAULT_SOURCE_PRECEDENCE] },
    disabled: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }) {

const props = __props;





const orderedKinds = computed(() => sanitizeSourcePrecedence(props.modelValue))

/** A lista é de strings: a chave do `draggable` é o próprio valor. */
const itemKey = (kind: EffectiveSourceKind) => kind

const applyOrder = (value: EffectiveSourceKind[]) => {
  if (props.disabled) return
  emit('update:modelValue', sanitizeSourcePrecedence(value))
}

const move = (from: number, to: number) => {
  if (to < 0 || to >= orderedKinds.value.length) return
  const next = [...orderedKinds.value]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  applyOrder(next)
}

const onItemKeydown = (event: KeyboardEvent, index: number) => {
  if (props.disabled) return
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
  event.preventDefault()
  move(index, event.key === 'ArrowUp' ? index - 1 : index + 1)
}

/** A linha travada engole as setas: mexer nela não pode emitir ordem nenhuma. */
const onPinnedKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') event.preventDefault()
}

const restoreDefault = () => applyOrder([...DEFAULT_SOURCE_PRECEDENCE])

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", {
      class: "rounded-lg border border-gray-200 overflow-hidden",
      "data-testid": "source-precedence-list",
      "data-draggable": String(!_ctx.disabled),
      role: "list"
    }, [
      _createVNode(_unref(draggable), {
        "model-value": orderedKinds.value,
        "item-key": itemKey,
        handle: ".source-precedence-handle",
        "ghost-class": "bg-primary5",
        animation: "200",
        disabled: _ctx.disabled,
        tag: "div",
        "onUpdate:modelValue": applyOrder
      }, {
        item: _withCtx(({ element, index }) => [
          _createElementVNode("div", {
            class: "flex items-center gap-3 border-b border-gray-100 bg-white px-3 py-2.5 last:border-b-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary20",
            role: "listitem",
            "data-testid": `source-precedence-item-${element}`,
            draggable: !_ctx.disabled,
            tabindex: _ctx.disabled ? -1 : 0,
            "aria-label": `${_unref(SOURCE_PRECEDENCE_NAMES)[element as EffectiveSourceKind]}, posição ${index + 1} de ${orderedKinds.value.length + 1}`,
            onKeydown: ($event: any) => (onItemKeydown($event, index))
          }, [
            _createVNode(SVGIcon, {
              path: _unref(mdiDragVertical),
              "aria-hidden": "true",
              class: _normalizeClass(["source-precedence-handle h-5 w-5 shrink-0 text-neutralLight", _ctx.disabled ? 'opacity-30' : 'cursor-grab'])
            }, null, 8, ["path", "class"]),
            _createElementVNode("span", _hoisted_5, _toDisplayString(index + 1), 1),
            _createElementVNode("span", _hoisted_6, [
              _createElementVNode("span", {
                class: "text-sm font-semibold text-neutral50",
                "data-testid": `source-precedence-item-${element}-name`
              }, _toDisplayString(_unref(SOURCE_PRECEDENCE_NAMES)[element as EffectiveSourceKind]), 9, _hoisted_7),
              _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(SOURCE_PRECEDENCE_DESCRIPTIONS)[element as EffectiveSourceKind]), 1)
            ])
          ], 40, _hoisted_4)
        ]),
        _: 1
      }, 8, ["model-value", "disabled"]),
      _createElementVNode("div", {
        class: "flex items-center gap-3 border-t border-gray-100 bg-gray-50 px-3 py-2.5 text-neutralLight",
        role: "listitem",
        "data-testid": `source-precedence-item-${_unref(PINNED_SOURCE_KIND)}`,
        draggable: "false",
        tabindex: "-1",
        onKeydown: onPinnedKeydown
      }, [
        _createVNode(SVGIcon, {
          path: _unref(mdiLock),
          class: "h-5 w-5 shrink-0 opacity-40"
        }, null, 8, ["path"]),
        _createElementVNode("span", _hoisted_10, _toDisplayString(orderedKinds.value.length + 1), 1),
        _createElementVNode("span", _hoisted_11, [
          _createElementVNode("span", {
            class: "text-sm font-medium text-gray-500",
            "data-testid": `source-precedence-item-${_unref(PINNED_SOURCE_KIND)}-name`
          }, _toDisplayString(_unref(SOURCE_PRECEDENCE_NAMES)[_unref(PINNED_SOURCE_KIND)]), 9, _hoisted_12),
          _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(SOURCE_PRECEDENCE_DESCRIPTIONS)[_unref(PINNED_SOURCE_KIND)]), 1)
        ])
      ], 40, _hoisted_9)
    ], 8, _hoisted_3),
    (!_ctx.disabled)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createElementVNode("button", {
            type: "button",
            class: "rounded-md border border-primary30 px-3 py-1.5 text-xs font-bold text-primary30",
            "data-testid": "source-precedence-restore-default",
            onClick: restoreDefault
          }, " ↺ Restaurar padrão "),
          _hoisted_15
        ]))
      : _createCommentVNode("", true),
    _hoisted_16
  ]))
}
}

})